import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { useNetto } from './netto';
import { formatMoney } from './utils';

export type GeldModus = 'bruto' | 'netto';

const KEY = 'trigion-geld-modus';
const Ctx = createContext<{ modus: GeldModus; setModus: (m: GeldModus) => void } | null>(null);

function lees(): GeldModus {
  try {
    return localStorage.getItem(KEY) === 'netto' ? 'netto' : 'bruto';
  } catch {
    return 'bruto';
  }
}

/** Eén keuze voor de hele app: verdiensten tonen als bruto of als (geschat) netto. */
export function GeldProvider({ children }: { children: ReactNode }) {
  const [modus, set] = useState<GeldModus>(lees);
  const setModus = useCallback((m: GeldModus) => {
    set(m);
    try {
      localStorage.setItem(KEY, m);
    } catch {
      /* privévenster: dan alleen voor deze sessie */
    }
  }, []);
  const value = useMemo(() => ({ modus, setModus }), [modus, setModus]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/**
 * Verdiensten in de gekozen modus. `bedrag(bruto, datum)` geeft het getal (netto = bruto × de
 * netto/bruto-verhouding van die loonperiode), `geld(...)` de opgemaakte tekst, `label` "Bruto"/"Netto".
 * Niet gebruiken voor loonstrookvergelijkingen (altijd bruto) of de uitbetaling (altijd netto).
 */
export function useGeld() {
  const ctx = useContext(Ctx);
  const modus = ctx?.modus ?? 'bruto';
  const nettoVan = useNetto();
  const bedrag = useCallback(
    (bruto: number | null | undefined, date: string): number | null => {
      if (bruto == null) return null;
      return modus === 'bruto' ? bruto : nettoVan(bruto, date);
    },
    [modus, nettoVan]
  );
  const geld = useCallback(
    (bruto: number | null | undefined, date: string): string => {
      const n = bedrag(bruto, date);
      return n == null ? '–' : formatMoney(n);
    },
    [bedrag]
  );
  return {
    modus,
    setModus: ctx?.setModus ?? (() => {}),
    bedrag,
    geld,
    label: modus === 'bruto' ? 'Bruto' : 'Netto',
  };
}

/**
 * De schakelaar rechtsboven: één knop, elke tik wisselt tussen bruto en netto. Het actieve
 * label staat op een schuivend vlak, zodat je in één oogopslag ziet welke stand aan staat.
 */
export function GeldSchakelaar() {
  const { modus, setModus } = useGeld();
  const netto = modus === 'netto';
  return (
    <button
      type="button"
      role="switch"
      aria-checked={netto}
      aria-label="Bedragen netto tonen"
      title="Netto is een benadering: bruto min loonheffing en premies van die loonperiode."
      onClick={() => setModus(netto ? 'bruto' : 'netto')}
      className="relative grid h-8 w-[7.5rem] shrink-0 grid-cols-2 items-center rounded-full border border-surface-line bg-surface p-0.5 text-xs font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-300"
    >
      <span
        aria-hidden
        className={
          'absolute inset-y-0.5 left-0.5 w-[calc(50%-2px)] rounded-full bg-slate-100 shadow-sm transition-transform duration-base ease-calm motion-reduce:transition-none ' +
          (netto ? 'translate-x-full' : 'translate-x-0')
        }
      />
      <span className={'relative z-10 transition-colors duration-base ' + (netto ? 'text-slate-400' : 'text-slate-900')}>Bruto</span>
      <span className={'relative z-10 transition-colors duration-base ' + (netto ? 'text-slate-900' : 'text-slate-400')}>Netto</span>
    </button>
  );
}

/** Een verdienste in de gekozen modus. `date` bepaalt de loonperiode voor het netto. */
export function Geld({ bruto, date }: { bruto: number | null | undefined; date: string }) {
  const { geld } = useGeld();
  return <>{geld(bruto, date)}</>;
}
